import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CreditCard, Loader2, Send, ShoppingBasket, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import A2BPaymentForm from '@/components/a2b/A2BPaymentForm';
import { capitalizeWords } from '@/lib/utils';
import { trackSongInteraction, resolveArtistLiveSessionId } from '@/lib/viewerTracking';
import { useToast } from '@/components/ui/use-toast';
import { isPayPalCheckoutCountry } from '@/lib/paypalCheckout';
import { db } from '@/lib/firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const PayPalUSProcessor = lazy(() => import('@/components/paypal/PayPalUSProcessor.jsx'));

function combinedSongTitle(songs) {
  return songs.map((song) => song.title).filter(Boolean).join(' + ');
}

function combinedOriginalArtist(songs) {
  const names = [...new Set(
    songs.map((song) => song.original_artist || song.artist || 'Unknown Artist')
  )];
  return names.join(' / ');
}

export function RequestBasketCheckout({
  open,
  onClose,
  songs,
  onRemoveSong,
  artistId,
  sessionId,
  artistCurrency,
  artistCountry,
  format,
  onPaid,
}) {
  const { toast } = useToast();
  const usePayPal = isPayPalCheckoutCountry(artistCountry);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    requester_name: '',
    requester_email: '',
    note: '',
    tip: '',
  });

  const songsTotal = useMemo(
    () => songs.reduce((sum, song) => sum + (Number(song.price) || 0), 0),
    [songs]
  );
  const tipAmount = parseFloat(formData.tip) || 0;
  const grandTotal = songsTotal + tipAmount;

  useEffect(() => {
    if (!open) {
      setShowPaymentModal(false);
      return undefined;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  useEffect(() => {
    if (open && songs.length === 0) onClose();
  }, [open, songs.length, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const paymentRequestData = useMemo(() => {
    const first = songs[0];
    return {
      songId: first?.id || null,
      songTitle: combinedSongTitle(songs) || first?.title || null,
      originalArtist: combinedOriginalArtist(songs) || 'Unknown Artist',
      requesterName: formData.requester_name || 'Anonymous',
      requesterEmail: formData.requester_email || null,
      note: formData.note || '',
      sessionId: sessionId || null,
      tipAmount,
    };
  }, [songs, formData.requester_name, formData.requester_email, formData.note, sessionId, tipAmount]);

  const paymentDescription = useMemo(() => {
    const titles = songs.map((song) => capitalizeWords(song.title)).join(', ');
    const n = songs.length;
    return `${n === 1 ? 'Song request' : 'Song requests'}: ${titles}${
      tipAmount ? ` + ${format(tipAmount)} tip` : ''
    }`;
  }, [songs, tipAmount, format]);

  const submitFreeRequests = async () => {
    if (!artistId || songs.length === 0) return false;
    setSubmitting(true);
    try {
      const first = songs[0];
      await addDoc(collection(db, 'requests'), {
        artist_id: artistId,
        session_id: sessionId || null,
        song_id: first.id,
        song_title: combinedSongTitle(songs),
        original_artist: combinedOriginalArtist(songs),
        requester_name: formData.requester_name,
        requester_email: formData.requester_email || '',
        note: formData.note || '',
        status: 'pending',
        amount_paid: 0,
        tip: 0,
        is_tip_only: false,
        created_at: serverTimestamp(),
      });
      toast({
        title: 'Request sent',
        description:
          songs.length === 1
            ? `Your request for "${capitalizeWords(songs[0].title)}" is in the queue.`
            : `${songs.length} songs have been sent to the artist as one request.`,
      });
      onPaid();
      return true;
    } catch (error) {
      console.error('Basket free request failed:', error);
      toast({
        title: 'Could not send request',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!formData.requester_name.trim()) return;
    if (grandTotal > 0) {
      setShowPaymentModal(true);
      return;
    }
    await submitFreeRequests();
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    for (const song of songs) {
      if (!artistId || !song?.id) continue;
      let sid = sessionId;
      if (!sid) sid = await resolveArtistLiveSessionId(artistId);
      trackSongInteraction(artistId, song.id, 'paid', sid);
    }
    toast({
      title: 'Payment complete',
      description:
        songs.length === 1
          ? 'Your song request is in the queue.'
          : `${songs.length} song requests are in the queue.`,
    });
    onPaid();
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <AnimatePresence>
        {open && !showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-[100000] overflow-y-auto overscroll-contain bg-black/85 px-4 py-4 backdrop-blur-sm"
            style={{ WebkitOverflowScrolling: 'touch' }}
            onClick={onClose}
          >
            <motion.div
              initial={{ y: 80, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 80, opacity: 0, scale: 0.98 }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              className="relative mx-auto w-full max-w-md rounded-2xl border border-gray-600 bg-gray-900 p-6"
              style={{ paddingBottom: '80px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Close basket"
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800 text-gray-300 transition-colors hover:bg-gray-700 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="mb-5 flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600">
                  <ShoppingBasket className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Your basket</h2>
                  <p className="text-sm text-violet-200">
                    {songs.length === 1 ? '1 song selected' : `${songs.length} songs selected`}
                  </p>
                </div>
              </div>

              <ul className="mb-5 space-y-2">
                {songs.map((song) => (
                  <li
                    key={song.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{capitalizeWords(song.title)}</p>
                      <p className="truncate text-xs text-gray-400">
                        {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="tabular-nums text-sm font-semibold text-emerald-300">
                        {format(Number(song.price) || 0)}
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemoveSong(song.id)}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-red-300"
                        aria-label={`Remove ${song.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <form onSubmit={handleContinue} className="space-y-4">
                <div>
                  <Label htmlFor="basket-name" className="text-sm font-medium text-gray-200">
                    Your Name
                  </Label>
                  <Input
                    id="basket-name"
                    name="requester_name"
                    value={formData.requester_name}
                    onChange={handleInputChange}
                    required
                    className="mt-1 border-gray-600 bg-gray-800 text-base text-white"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div>
                  <Label htmlFor="basket-email" className="text-sm font-medium text-gray-200">
                    Email (optional)
                  </Label>
                  <Input
                    id="basket-email"
                    name="requester_email"
                    type="email"
                    value={formData.requester_email}
                    onChange={handleInputChange}
                    className="mt-1 border-gray-600 bg-gray-800 text-base text-white"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div>
                  <Label htmlFor="basket-note" className="text-sm font-medium text-gray-200">
                    Note (optional)
                  </Label>
                  <Textarea
                    id="basket-note"
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    className="mt-1 resize-none overflow-y-auto border-gray-600 bg-gray-800 text-base text-white"
                    style={{ fontSize: '16px', minHeight: '40px', maxHeight: '120px', height: '40px' }}
                    rows={1}
                    onInput={(e) => {
                      e.target.style.height = '40px';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="basket-tip" className="text-sm font-medium text-gray-200">
                    Tip (optional)
                  </Label>
                  <Input
                    id="basket-tip"
                    name="tip"
                    type="number"
                    step="0.01"
                    value={formData.tip}
                    onChange={handleInputChange}
                    placeholder="e.g., 5.00"
                    className="mt-1 border-gray-600 bg-gray-800 text-base text-white"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div className="rounded-xl border border-blue-500/30 bg-blue-500/20 p-4 text-center">
                  <p className="mb-1 text-sm text-blue-200">
                    {songs.length > 1 ? 'One payment for all songs' : 'Total Amount'}
                  </p>
                  <p className="text-2xl font-bold text-white">{format(grandTotal)}</p>
                </div>
                <Button
                  type="submit"
                  disabled={submitting || songs.length === 0}
                  className="mt-4 w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white shadow-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : grandTotal > 0 ? (
                    <CreditCard className="mr-2 h-4 w-4" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {submitting
                    ? 'Sending...'
                    : grandTotal > 0
                      ? 'Continue to Payment'
                      : songs.length === 1
                        ? 'Send Request'
                        : 'Send Requests'}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showPaymentModal && grandTotal > 0 && (
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="mx-auto max-h-[90vh] max-w-md overflow-y-auto border-gray-600 bg-gray-900 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <CreditCard className="mr-2 h-5 w-5" />
                {usePayPal ? 'Pay with PayPal' : 'Complete Payment'}
              </DialogTitle>
              <DialogDescription>
                {usePayPal ? (
                  <>
                    {format(grandTotal)} — complete checkout in the secure PayPal window
                    {songs.length === 1
                      ? ` for “${capitalizeWords(songs[0].title)}”`
                      : ` for ${songs.length} songs`}
                  </>
                ) : (
                  <>
                    Pay {format(grandTotal)} to request{' '}
                    {songs.length === 1
                      ? `“${capitalizeWords(songs[0].title)}”`
                      : `${songs.length} songs`}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {usePayPal ? (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Loading checkout…
                  </div>
                }
              >
                <PayPalUSProcessor
                  artistCountry={artistCountry}
                  artistId={artistId}
                  amount={grandTotal}
                  description={paymentDescription}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowPaymentModal(false)}
                  mode="artist"
                  requestData={paymentRequestData}
                />
              </Suspense>
            ) : (
              <A2BPaymentForm
                amount={grandTotal}
                artistId={artistId}
                description={paymentDescription}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowPaymentModal(false)}
                mode="artist"
                currency={artistCurrency || undefined}
                requestData={paymentRequestData}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>,
    document.body
  );
}
